import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, MapPin, Youtube, Newspaper } from 'lucide-react';
import { TermsModal } from '@/components/auth/TermsModal';
import { trackEvent } from '@/lib/analytics';
import { useLanguage } from '@/contexts/LanguageContext';

const FOOTER_COPY = {
  ko: {
    tagline: (<>AI 기반 이미지 ALT 텍스트 자동 생성 솔루션으로<br />웹 접근성을 향상하고 SEO를 최적화합니다.</>),
    serviceHeading: '서비스',
    features: '주요 기능',
    pricing: '요금제',
    startFree: '무료 시작하기',
    login: '로그인',
    companyHeading: '회사',
    about: '회사 소개',
    contact: '문의하기',
    address: '서울 중구 다산로 106 (신당동, 한두빌딩) 5층',
    companyInfo: '상호명: (주)지호아이앤씨 | 대표이사: 최정윤 | 사업자등록번호: 849-81-00345',
    terms: '이용약관',
    privacy: '개인정보처리방침',
  },
  en: {
    tagline: (<>AI-powered image ALT text generation<br />to improve web accessibility and optimize SEO.</>),
    serviceHeading: 'Service',
    features: 'Features',
    pricing: 'Pricing',
    startFree: 'Start Free',
    login: 'Log In',
    companyHeading: 'Company',
    about: 'About',
    contact: 'Contact',
    address: '5F, 106 Dasan-ro, Jung-gu, Seoul, South Korea',
    companyInfo: 'Company: JIHO I&C Co., Ltd. | CEO: Choi Jeong-yun | Business Reg. No.: 849-81-00345',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
  },
} as const;

export function Footer() {
  const { language } = useLanguage();
  const t = FOOTER_COPY[language];
  const [showTermsModal, setShowTermsModal] = useState(false);
  const currentYear = new Date().getFullYear();
  const [, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleScrollTo = (sectionId: string) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const trackSocialClick = (channel: string) => trackEvent('footer_social_click', { channel });

  return (
    <>
      <footer className="bg-gray-900 text-gray-300">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* 회사 정보 */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="h-8 w-8" />
                <span className="text-xl font-bold text-white">ALT Generation</span>
              </div>
              <p className="text-sm mb-4 text-gray-400">
                {t.tagline}
              </p>
              <div className="flex items-center gap-3 mb-4">
                <a
                  href="https://www.youtube.com/@altgeneration-o6u"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ALT Generation 유튜브 채널"
                  onClick={() => trackSocialClick('youtube')}
                  className="flex items-center justify-center h-9 w-9 rounded-full bg-gray-800 text-gray-400 hover:bg-primary hover:text-white transition-colors"
                >
                  <Youtube className="h-4 w-4" />
                </a>
                <a
                  href="https://info87382.tistory.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="ALT Generation 블로그"
                  onClick={() => trackSocialClick('blog')}
                  className="flex items-center justify-center h-9 w-9 rounded-full bg-gray-800 text-gray-400 hover:bg-primary hover:text-white transition-colors"
                >
                  <Newspaper className="h-4 w-4" />
                </a>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>{t.address}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span>alt.generation@outlook.com</span>
                </div>
              </div>
            </div>

            {/* 서비스 링크 */}
            <div>
              <h3 className="text-white font-semibold mb-4">{t.serviceHeading}</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => handleScrollTo('features')}
                    className="text-sm hover:text-primary transition-colors"
                  >
                    {t.features}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleScrollTo('pricing')}
                    className="text-sm hover:text-primary transition-colors"
                  >
                    {t.pricing}
                  </button>
                </li>
                <li>
                  <Link to="/signup" className="text-sm hover:text-primary transition-colors">
                    {t.startFree}
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-sm hover:text-primary transition-colors">
                    {t.login}
                  </Link>
                </li>
              </ul>
            </div>

            {/* 회사 */}
            <div>
              <h3 className="text-white font-semibold mb-4">{t.companyHeading}</h3>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleScrollTo('about');
                    }}
                    className="text-sm hover:text-primary transition-colors"
                  >
                    {t.about}
                  </button>
                </li>
                <li>
                  <a href="mailto:alt.generation@outlook.com" className="text-sm hover:text-primary transition-colors">
                    {t.contact}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* 하단 영역 */}
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              {/* 회사 정보 */}
              <div className="text-xs text-gray-500 text-center md:text-left">
                <p>{t.companyInfo}</p>
                {/* <p className="mt-1">통신판매업신고번호: 경주 제03-34호</p> */}
              </div>

              {/* 법적 링크 */}
              <div className="flex gap-4">
                <button
                  onClick={() => setShowTermsModal(true)}
                  className="text-xs text-gray-400 hover:text-primary transition-colors underline"
                >
                  {t.terms}
                </button>
                <Link
                  to="/privacy"
                  className="text-xs text-gray-400 hover:text-primary transition-colors underline font-semibold"
                >
                  {t.privacy}
                </Link>
              </div>
            </div>

            {/* 저작권 */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                © {currentYear} ALT Generation. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* 약관 모달 */}
      {showTermsModal && <TermsModal open={showTermsModal} onOpenChange={setShowTermsModal} />}
    </>
  );
}
